import { useState } from "react";
import { useApi } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";

type RuleKind = "from_domain" | "from_address" | "subject_contains";

export interface AddRuleFromTicketProps {
  ticketId: string;
  // Suggested defaults sourced from the ticket's first inbound
  // message. We pre-pick a sensible kind and pattern, but the
  // operator can switch kind and edit the pattern before saving.
  defaults: { fromEmail: string | null; subject: string | null };
  onClose: () => void;
  onCreated: () => void;
}

// Shown when the operator clicks "Auto-archive matches like this".
// Lets them pick a rule kind (from-domain / from-address / subject-
// substring), pre-fills the pattern from the ticket, and offers an
// "also close this ticket now" checkbox so a single click handles
// both the rule and the ticket on screen.
export function AddRuleFromTicket({ ticketId, defaults, onClose, onCreated }: AddRuleFromTicketProps) {
  const t = useI18n().t;
  const api = useApi();

  const domainFromEmail = (defaults.fromEmail ?? "").includes("@")
    ? (defaults.fromEmail ?? "").split("@").pop() ?? ""
    : "";

  const [kind, setKind] = useState<RuleKind>(domainFromEmail ? "from_domain" : "subject_contains");
  const [pattern, setPattern] = useState(domainFromEmail || defaults.subject || "");
  const [applyToTicket, setApplyToTicket] = useState(true);
  const [applyToInbox, setApplyToInbox] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function suggestedFor(nextKind: RuleKind): string {
    if (nextKind === "from_domain") return domainFromEmail;
    if (nextKind === "from_address") return defaults.fromEmail ?? "";
    return defaults.subject ?? "";
  }

  function pickKind(nextKind: RuleKind) {
    setKind(nextKind);
    // Only overwrite the pattern when the user hasn't typed over the
    // previous suggestion — compare against the suggestion for the
    // *old* kind rather than the empty string.
    const previousSuggestion = suggestedFor(kind);
    if (pattern.trim() === "" || pattern === previousSuggestion) {
      setPattern(suggestedFor(nextKind));
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pattern.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<{ sweep?: { closed: number; scanned: number } }>("/api/match-rules", {
        kind,
        pattern: pattern.trim(),
        note: t("autoArchive.noteFromTicket", { ticketId: ticketId.slice(0, 8) }),
        applyToTicketId: applyToTicket ? ticketId : undefined,
        applyToInbox,
      });
      // The sweep count comes back on success — leave it for the parent
      // to show if it wants; the detail page just reloads the ticket.
      void res;
      onCreated();
      onClose();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes("409") && msg.includes("RULE_EXISTS")) {
        setError(t("autoArchive.duplicateError"));
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-panel">
        <h3 style={{ marginTop: 0 }}>{t("autoArchive.addFromTicketTitle")}</h3>
        <p style={{ color: "var(--text-muted)" }}>{t("autoArchive.addFromTicketBody")}</p>

        <form onSubmit={submit}>
          <div className="field">
            <label>{t("autoArchive.kindLabel")}</label>
            <select value={kind} onChange={(e) => pickKind(e.target.value as RuleKind)}>
              <option value="from_domain" disabled={!domainFromEmail}>{t("autoArchive.kind.from_domain")}</option>
              <option value="from_address" disabled={!defaults.fromEmail}>{t("autoArchive.kind.from_address")}</option>
              <option value="subject_contains" disabled={!defaults.subject}>{t("autoArchive.kind.subject_contains")}</option>
            </select>
          </div>
          <div className="field">
            <label>{t("autoArchive.patternLabel")}</label>
            <input value={pattern} onChange={(e) => setPattern(e.target.value)} required />
            <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>
              {t(`autoArchive.help.${kind}`)}
            </small>
          </div>
          <div className="field">
            <label className="inline-checkbox">
              <input
                type="checkbox"
                checked={applyToTicket}
                onChange={(e) => setApplyToTicket(e.target.checked)}
              />
              {t("autoArchive.alsoCloseThisTicket")}
            </label>
          </div>
          <div className="field">
            <label className="inline-checkbox">
              <input
                type="checkbox"
                checked={applyToInbox}
                onChange={(e) => setApplyToInbox(e.target.checked)}
              />
              {t("autoArchive.applyToInbox")}
            </label>
            <small style={{ color: "var(--text-muted)", display: "block", marginTop: 4 }}>
              {t("autoArchive.applyToInboxHelp")}
            </small>
          </div>
          {error && <div style={{ color: "var(--danger)", marginBottom: 8 }}>{error}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" onClick={onClose}>{t("common.cancel")}</button>
            <button type="submit" className="primary" disabled={saving || !pattern.trim()}>
              {saving ? t("common.saving") : t("autoArchive.addButton")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
